import { useCallback, useEffect, useRef, useState } from 'react'
import { Route, Routes, useNavigate, useParams } from 'react-router-dom'
import type { PrerequisiteGate } from '@assessments/shared'
import { AssessmentOverview } from '../features/assessment-overview/AssessmentOverview.js'
import { AssessmentSidebar } from '../features/assessment-overview/AssessmentSidebar.js'
import { useAssessmentOverview } from '../features/assessment-overview/useAssessmentOverview.js'
import { AcceptTerms } from '../features/accept-terms/AcceptTerms.js'
import { GuidancePage } from '../features/guidance/GuidancePage.js'
import { SectionView, type SectionExit } from '../features/section-view/SectionView.js'
import { HomePage } from '../pages/HomePage.js'
import { NotFoundPage } from '../pages/NotFoundPage.js'
import { RequireTermsAccepted } from './RequireTermsAccepted.js'

/**
 * Default landing for an in-progress assessment: the overview screen.
 *
 * `dismissedWelcomes` lives in the parent `AppRoutes` (not local state) so it survives
 * this route remounting when a section is opened and later exited back here — otherwise
 * "Done editing"/back-to-overview would re-show the welcome instead of the section list.
 */
function AssessmentOverviewRoute({
  dismissedWelcomes,
  onDismissWelcome,
}: {
  dismissedWelcomes: ReadonlySet<string>
  onDismissWelcome: (organizationSurveyId: string) => void
}) {
  const { organizationSurveyId = '' } = useParams()
  const navigate = useNavigate()
  return (
    // Keyed on organizationSurveyId so a param-only change remounts (rather than
    // reuses) the component — otherwise its local welcomeDismissed state, seeded
    // from the prop only at mount, would leak from one assessment into another.
    <AssessmentOverview
      key={organizationSurveyId}
      organizationSurveyId={organizationSurveyId}
      welcomeDismissed={dismissedWelcomes.has(organizationSurveyId)}
      onWelcomeDismissed={() => onDismissWelcome(organizationSurveyId)}
      onOpenSection={(sectionId) =>
        navigate(`/assessments/${organizationSurveyId}/sections/${sectionId}`)
      }
      onOpenTerms={() => navigate(`/assessments/${organizationSurveyId}/terms`)}
      onOpenGuidance={() => navigate(`/assessments/${organizationSurveyId}/guidance`)}
      onBackToList={() => navigate('/')}
    />
  )
}

/**
 * Accept Terms of Use screen, reached via the overview prerequisite gate. Once
 * accepted it is a read-only prerequisite, so it sits in the sidebar layout like a
 * section; an outstanding gate stays a bare interstitial.
 */
function AcceptTermsRoute() {
  const { organizationSurveyId = '' } = useParams()
  const navigate = useNavigate()
  const { data, loading } = useAssessmentOverview(organizationSurveyId)
  // The hook keeps the previous assessment's data while a new id loads.
  const overview = data?.organizationSurveyId === organizationSurveyId ? data : null
  const toOverview = () => navigate(`/assessments/${organizationSurveyId}`)

  const terms = (
    <AcceptTerms
      organizationSurveyId={organizationSurveyId}
      onAccepted={toOverview}
      onBack={toOverview}
    />
  )
  const termsAccepted = overview?.prerequisites.some(
    (p) => p.kind === 'terms_and_conditions' && p.complete,
  )
  // Holding back until the overview resolves avoids rendering the bare screen and then
  // remounting it inside the sidebar layout for an accepted respondent.
  if (loading && !overview) {
    return <main className="min-w-0 p-1 text-sm text-subtle">Loading terms…</main>
  }
  if (!overview || !termsAccepted) return terms

  const openPrerequisite = (gate: PrerequisiteGate) => {
    if (gate.sectionId !== null) {
      navigate(`/assessments/${organizationSurveyId}/sections/${gate.sectionId}`)
    }
  }

  return (
    <div className="flex min-h-[calc(100vh-4rem)] bg-surface-page">
      <AssessmentSidebar
        overview={overview}
        activePrerequisiteKind="terms_and_conditions"
        onOpenSection={(id) => navigate(`/assessments/${organizationSurveyId}/sections/${id}`)}
        onOpenOverview={toOverview}
        onOpenPrerequisite={openPrerequisite}
        onOpenGuidance={() => navigate(`/assessments/${organizationSurveyId}/guidance`)}
        onBackToList={() => navigate('/')}
      />
      <div className="min-w-0 flex-1 px-6 py-10 lg:px-10">{terms}</div>
    </div>
  )
}

/**
 * Section view, guarded by the T&C prerequisite: direct navigation to a gated
 * section without an accepted T&C is redirected to the accept screen.
 *
 * Being inside a section implies the assessment's welcome has already been passed
 * (or was skipped via a direct link) — marking it dismissed here means a return trip
 * to the overview (e.g. Done editing) lands on the section list even for a caller who
 * deep-linked straight into a section without visiting the overview first.
 */
function SectionViewRoute({
  onDismissWelcome,
}: {
  onDismissWelcome: (organizationSurveyId: string) => void
}) {
  const { organizationSurveyId = '', sectionId = '' } = useParams()
  const navigate = useNavigate()
  useEffect(() => {
    onDismissWelcome(organizationSurveyId)
  }, [organizationSurveyId, onDismissWelcome])

  // Every sidebar destination unmounts the section (or swaps its lock), so it leaves
  // through the section's own exit rather than navigating out from under it.
  const sectionExit = useRef<SectionExit | null>(null)
  const leaveTo = (path: string) => {
    if (sectionExit.current) void sectionExit.current(path)
    else navigate(path)
  }

  const openPrerequisite = (gate: PrerequisiteGate) => {
    if (gate.sectionId !== null) {
      leaveTo(`/assessments/${organizationSurveyId}/sections/${gate.sectionId}`)
    } else if (gate.kind === 'terms_and_conditions') {
      leaveTo(`/assessments/${organizationSurveyId}/terms`)
    }
  }

  return (
    <RequireTermsAccepted organizationSurveyId={organizationSurveyId}>
      {(overview) => (
        <div className="flex min-h-[calc(100vh-4rem)] bg-surface-page">
          <AssessmentSidebar
            overview={overview}
            activeSectionId={sectionId}
            onOpenSection={(id) => leaveTo(`/assessments/${organizationSurveyId}/sections/${id}`)}
            onOpenOverview={() => leaveTo(`/assessments/${organizationSurveyId}`)}
            onOpenPrerequisite={openPrerequisite}
            onOpenGuidance={() => leaveTo(`/assessments/${organizationSurveyId}/guidance`)}
            onBackToList={() => leaveTo('/')}
          />
          {/* Keyed per section so a switch remounts it: per-section state (e.g. a rejected
            Mark complete's required-question list) must not carry into the next section. */}
          <SectionView
            key={`${organizationSurveyId}/${sectionId}`}
            organizationSurveyId={organizationSurveyId}
            sectionId={sectionId}
            exitRef={sectionExit}
          />
        </div>
      )}
    </RequireTermsAccepted>
  )
}

/**
 * The assessment's authored instructions, reachable from the sidebar on every assessment
 * screen. Read-only, so it is not held behind the Terms of Use gate.
 */
function GuidanceRoute() {
  const { organizationSurveyId = '' } = useParams()
  const navigate = useNavigate()
  const { data, loading } = useAssessmentOverview(organizationSurveyId)
  // The hook keeps the previous assessment's data while a new id loads.
  const overview = data?.organizationSurveyId === organizationSurveyId ? data : null
  const toOverview = () => navigate(`/assessments/${organizationSurveyId}`)

  // Only a first load can fail visibly; a failed background refresh keeps what is shown.
  if (!overview) {
    return loading ? (
      <main className="min-w-0 p-1 text-sm text-subtle">Loading guidance…</main>
    ) : (
      <main className="min-w-0 px-6 py-10 text-sm text-subtle">
        Couldn’t load the guidance for this assessment. Please try again.
      </main>
    )
  }

  const openPrerequisite = (gate: PrerequisiteGate) => {
    if (gate.sectionId !== null) {
      navigate(`/assessments/${organizationSurveyId}/sections/${gate.sectionId}`)
    } else if (gate.kind === 'terms_and_conditions') {
      navigate(`/assessments/${organizationSurveyId}/terms`)
    }
  }

  return (
    <div className="flex min-h-[calc(100vh-4rem)] bg-surface-page">
      <AssessmentSidebar
        overview={overview}
        activeGuidance
        onOpenSection={(id) => navigate(`/assessments/${organizationSurveyId}/sections/${id}`)}
        onOpenOverview={toOverview}
        onOpenPrerequisite={openPrerequisite}
        onBackToList={() => navigate('/')}
      />
      <GuidancePage instructions={overview.instructions} onBackToOverview={toOverview} />
    </div>
  )
}

export function AppRoutes() {
  const [dismissedWelcomes, setDismissedWelcomes] = useState<ReadonlySet<string>>(new Set())
  const onDismissWelcome = useCallback(
    (organizationSurveyId: string) =>
      setDismissedWelcomes((prev) => new Set(prev).add(organizationSurveyId)),
    [],
  )

  return (
    <Routes>
      <Route path="/" element={<HomePage />} />
      <Route
        path="/assessments/:organizationSurveyId"
        element={
          <AssessmentOverviewRoute
            dismissedWelcomes={dismissedWelcomes}
            onDismissWelcome={onDismissWelcome}
          />
        }
      />
      <Route path="/assessments/:organizationSurveyId/terms" element={<AcceptTermsRoute />} />
      <Route path="/assessments/:organizationSurveyId/guidance" element={<GuidanceRoute />} />
      <Route
        path="/assessments/:organizationSurveyId/sections/:sectionId"
        element={<SectionViewRoute onDismissWelcome={onDismissWelcome} />}
      />
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  )
}
